/**
 * A person's name, email and phone as staff type them (#731): the Add person sheet and the edit on a person added in
 * Keystone share the fields, the checks made before asking the server, and how a refusal names who is already there.
 */
import { useLocation } from "react-router-dom";
import type { ConflictPerson, PersonField } from "../../../api/people";
import { href } from "../../app/links";
import { cx, Field, focusRing, Input } from "../../kit";
import type { Draft, FieldErrors } from "./personDraft";

const FIELDS: ReadonlyArray<{ key: PersonField; label: string; type: string; autoComplete: string; hint?: string }> = [
  { key: "first_name", label: "First name", type: "text", autoComplete: "off" },
  { key: "last_name", label: "Last name", type: "text", autoComplete: "off" },
  { key: "email", label: "Email", type: "email", autoComplete: "off" },
  { key: "phone", label: "Phone", type: "tel", autoComplete: "off", hint: "Optional" },
];

interface PersonFieldsProps {
  draft: Draft;
  errors: FieldErrors;
  onChange: (key: PersonField, value: string) => void;
  /** Under the email field: who already has it. */
  emailOwners?: ConflictPerson[];
  disabled?: boolean;
}

export function PersonFields({ draft, errors, onChange, emailOwners, disabled }: PersonFieldsProps) {
  return (
    <div className="flex w-full flex-col gap-4">
      {FIELDS.map(({ key, label, type, autoComplete, hint }) => (
        <div key={key} className="flex flex-col gap-2">
          <Field label={label} hint={hint} error={errors[key]}>
            <Input
              type={type}
              name={key}
              value={draft[key]}
              autoComplete={autoComplete}
              spellCheck={false}
              disabled={disabled}
              aria-invalid={errors[key] ? true : undefined}
              onChange={(event) => onChange(key, event.target.value)}
            />
          </Field>
          {key === "email" && emailOwners && emailOwners.length > 0 && <PeopleLinks people={emailOwners} />}
        </div>
      ))}
    </div>
  );
}

const LINK = cx("link-line rounded-sm", focusRing);

/** People already in Keystone, each a link that opens their sheet in this sheet's place, on this page, with their email. */
export function PeopleLinks({ people }: { people: ConflictPerson[] }) {
  const { pathname, search } = useLocation();
  const sheetOf = (id: number) => {
    const next = new URLSearchParams(search);
    next.delete("panel");
    next.set("person", String(id));
    return href(`${pathname}?${next.toString()}`);
  };
  return (
    <ul className="flex flex-col gap-2 text-body">
      {people.map((p) => (
        <li key={p.id} className="flex items-baseline justify-between gap-4">
          <a href={sheetOf(p.id)} data-name className={cx(LINK, "font-medium text-foreground")}>
            Open {p.name}
          </a>
          {p.email && <span className="min-w-0 truncate text-muted-foreground">{p.email}</span>}
        </li>
      ))}
    </ul>
  );
}
